// «Жизнь города» поверх слоёв Blender: мерцание факелов, дым из труб, прохожие на улицах, чайки над морем.
// Координаты факелов и труб приходят из того же рендера (layout.fx / sprite.fx), улицы — layout.streets.

export interface CityFx {
  torch?: number[][];
  smoke?: number[][];
}

export interface Walker {
  path: number[][];
  lengths: number[];
  total: number;
  speed: number;
  offset: number;
  shirt: string;
  hat: string | null;
  carry: boolean;
}

const SHIRTS = ['#7a2f22', '#2f4f7a', '#5d6b2c', '#8a6a2a', '#5a3a6e', '#6b4a32', '#3d6660'];

/** Детерминированные прохожие по улицам (одни и те же при каждой загрузке). */
export function makeWalkers(streets: number[][][] | undefined, count = 16): Walker[] {
  const usable = (streets ?? []).filter((s) => s.length >= 2);
  if (!usable.length) return [];
  const out: Walker[] = [];
  let seed = 7;
  const rnd = () => {
    seed = (seed * 9301 + 49297) % 233280;
    return seed / 233280;
  };
  for (let i = 0; i < count; i++) {
    const path = usable[i % usable.length]!;
    const lengths: number[] = [];
    let total = 0;
    for (let k = 1; k < path.length; k++) {
      const a = path[k - 1]!;
      const b = path[k]!;
      const d = Math.hypot((b[0] ?? 0) - (a[0] ?? 0), (b[1] ?? 0) - (a[1] ?? 0));
      lengths.push(d);
      total += d;
    }
    if (total < 40) continue;
    out.push({
      path,
      lengths,
      total,
      speed: 14 + rnd() * 12,
      offset: rnd() * total * 2,
      shirt: SHIRTS[Math.floor(rnd() * SHIRTS.length)]!,
      hat: rnd() < 0.4 ? '#4a3420' : null,
      carry: rnd() < 0.3,
    });
  }
  return out;
}

/** Позиция прохожего: туда-обратно по своей улице. */
export function walkerAt(w: Walker, time: number): { x: number; y: number; dir: number } {
  const cycle = (w.offset + time * w.speed) % (w.total * 2);
  const forward = cycle < w.total;
  let d = forward ? cycle : w.total * 2 - cycle;
  for (let k = 0; k < w.lengths.length; k++) {
    const len = w.lengths[k]!;
    if (d <= len || k === w.lengths.length - 1) {
      const a = w.path[k]!;
      const b = w.path[k + 1]!;
      const t = len > 0 ? Math.min(1, d / len) : 0;
      const ax = a[0] ?? 0;
      const ay = a[1] ?? 0;
      const bx = b[0] ?? 0;
      const by = b[1] ?? 0;
      return { x: ax + (bx - ax) * t, y: ay + (by - ay) * t, dir: (forward ? 1 : -1) * Math.sign(bx - ax || 1) };
    }
    d -= len;
  }
  const last = w.path[w.path.length - 1]!;
  return { x: last[0] ?? 0, y: last[1] ?? 0, dir: 1 };
}

export function drawWalker(ctx: CanvasRenderingContext2D, w: Walker, time: number) {
  const p = walkerAt(w, time);
  const step = Math.sin(time * w.speed * 0.55 + w.offset);
  ctx.save();
  ctx.translate(p.x, p.y);
  ctx.fillStyle = 'rgba(0,0,0,.28)';
  ctx.beginPath();
  ctx.ellipse(0, 0, 7, 3, 0, 0, Math.PI * 2);
  ctx.fill();
  // ноги
  ctx.strokeStyle = '#2b1d12';
  ctx.lineWidth = 2.4;
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(-1.5, -8);
  ctx.lineTo(-1.5 + step * 2.5, -1);
  ctx.moveTo(1.5, -8);
  ctx.lineTo(1.5 - step * 2.5, -1);
  ctx.stroke();
  // тело
  ctx.fillStyle = w.shirt;
  ctx.beginPath();
  ctx.roundRect(-4, -19, 8, 12, 3);
  ctx.fill();
  if (w.carry) {
    ctx.fillStyle = '#9a7442';
    ctx.fillRect(p.dir > 0 ? 3 : -8, -17, 5, 5);
  }
  // голова
  ctx.fillStyle = '#e2b48a';
  ctx.beginPath();
  ctx.arc(0, -22.5, 3.4, 0, Math.PI * 2);
  ctx.fill();
  if (w.hat) {
    ctx.fillStyle = w.hat;
    ctx.beginPath();
    ctx.ellipse(0, -25, 4.6, 1.8, 0, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();
}

const flicker = (time: number, i: number) =>
  0.72 + 0.16 * Math.sin(time * 11 + i * 1.7) + 0.12 * Math.sin(time * 23.3 + i * 0.6);

export function drawTorches(
  ctx: CanvasRenderingContext2D,
  pts: number[][] | undefined,
  time: number,
  base = 0,
  power = 1,
) {
  if (!pts?.length) return;
  ctx.save();
  ctx.globalCompositeOperation = 'lighter';
  pts.forEach((pt, i) => {
    const x = pt[0] ?? 0;
    const y = pt[1] ?? 0;
    const k = flicker(time, i + base) * power;
    const r = 16 * k;
    const g = ctx.createRadialGradient(x, y, 0, x, y, r);
    g.addColorStop(0, `rgba(255,190,90,${Math.min(0.9, 0.55 * k)})`);
    g.addColorStop(0.35, `rgba(255,120,30,${Math.min(0.5, 0.25 * k)})`);
    g.addColorStop(1, 'rgba(255,90,20,0)');
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
  });
  ctx.restore();
}

export function drawSmoke(ctx: CanvasRenderingContext2D, pts: number[][] | undefined, time: number) {
  if (!pts?.length) return;
  ctx.save();
  pts.forEach((pt, i) => {
    const x = pt[0] ?? 0;
    const y = pt[1] ?? 0;
    for (let k = 0; k < 6; k++) {
      const ph = (time * 0.22 + k / 6 + i * 0.37) % 1;
      const r = 4 + ph * 13;
      ctx.fillStyle = `rgba(215,215,210,${(1 - ph) * 0.32})`;
      ctx.beginPath();
      ctx.arc(x + Math.sin(ph * 3 + i) * 7 * ph + ph * 10, y - ph * 75, r, 0, Math.PI * 2);
      ctx.fill();
    }
  });
  ctx.restore();
}

/** Чайки над гаванью (нижняя часть картины). */
export function drawGulls(ctx: CanvasRenderingContext2D, w: number, h: number, time: number) {
  ctx.save();
  ctx.strokeStyle = 'rgba(250,250,245,.9)';
  ctx.lineWidth = 2;
  ctx.lineCap = 'round';
  for (let i = 0; i < 4; i++) {
    const span = w + 300;
    const x = ((time * (26 + i * 5) + i * 610) % span) - 150;
    const y = h * (0.8 + 0.04 * i) + Math.sin(time * 0.7 + i * 2) * 30;
    const flap = Math.sin(time * 6 + i) * 4;
    ctx.beginPath();
    ctx.moveTo(x - 9, y - flap);
    ctx.quadraticCurveTo(x - 4, y - 5, x, y);
    ctx.quadraticCurveTo(x + 4, y - 5, x + 9, y - flap);
    ctx.stroke();
  }
  ctx.restore();
}

/** Стройка: двое рабочих с молотками, искры от ударов, клубы пыли и полоса прогресса. */
export function drawConstruction(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  time: number,
  progress: number,
) {
  ctx.save();
  // пыль у основания
  for (let k = 0; k < 5; k++) {
    const ph = (time * 0.5 + k / 5) % 1;
    ctx.fillStyle = `rgba(190,165,120,${(1 - ph) * 0.35})`;
    ctx.beginPath();
    ctx.arc(x - 50 + k * 25 + ph * 10, y - 4 - ph * 22, 6 + ph * 10, 0, Math.PI * 2);
    ctx.fill();
  }
  // рабочие
  for (const [dx, dy, ph] of [
    [-44, 10, 0],
    [40, 4, 1.6],
  ] as const) {
    const px = x + dx;
    const py = y + dy;
    const swing = Math.max(0, Math.sin(time * 7 + ph));
    ctx.fillStyle = 'rgba(0,0,0,.28)';
    ctx.beginPath();
    ctx.ellipse(px, py, 7, 3, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#8a5a2a';
    ctx.beginPath();
    ctx.roundRect(px - 4, py - 19, 8, 12, 3);
    ctx.fill();
    ctx.strokeStyle = '#2b1d12';
    ctx.lineWidth = 2.4;
    ctx.beginPath();
    ctx.moveTo(px - 1.5, py - 8);
    ctx.lineTo(px - 1.5, py - 1);
    ctx.moveTo(px + 1.5, py - 8);
    ctx.lineTo(px + 1.5, py - 1);
    ctx.stroke();
    ctx.fillStyle = '#e2b48a';
    ctx.beginPath();
    ctx.arc(px, py - 22.5, 3.4, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#d8b04a';
    ctx.beginPath();
    ctx.ellipse(px, py - 25, 4.8, 2, 0, 0, Math.PI * 2);
    ctx.fill();
    // рука с молотом
    const ang = -1.9 + swing * 1.7;
    const hx = px + 3 + Math.cos(ang) * 11;
    const hy = py - 16 + Math.sin(ang) * 11;
    ctx.strokeStyle = '#6b4a2a';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(px + 3, py - 16);
    ctx.lineTo(hx, hy);
    ctx.stroke();
    ctx.fillStyle = '#555c60';
    ctx.fillRect(hx - 3, hy - 2, 6, 4);
    if (swing > 0.94) {
      for (let s = 0; s < 4; s++) {
        ctx.fillStyle = `rgba(255,${200 + s * 12},90,.9)`;
        ctx.beginPath();
        ctx.arc(hx + Math.cos(s * 1.6) * 7, hy + Math.sin(s * 1.6) * 5 - 3, 1.6, 0, Math.PI * 2);
        ctx.fill();
      }
    }
  }
  // прогресс
  ctx.fillStyle = 'rgba(10,20,34,.85)';
  ctx.beginPath();
  ctx.roundRect(x - 52, y + 22, 104, 10, 4);
  ctx.fill();
  const g = ctx.createLinearGradient(x - 50, 0, x + 50, 0);
  g.addColorStop(0, '#c98a2a');
  g.addColorStop(1, '#f3cf6a');
  ctx.fillStyle = g;
  ctx.beginPath();
  ctx.roundRect(x - 50, y + 24, 100 * Math.max(0.03, progress), 6, 3);
  ctx.fill();
  ctx.restore();
}

/** Улучшение завершено: золотые лучи и искры над участком (~2.5 с). */
export function drawLevelUp(ctx: CanvasRenderingContext2D, x: number, y: number, age: number) {
  if (age < 0 || age > 2.5) return;
  const k = age / 2.5;
  ctx.save();
  ctx.globalCompositeOperation = 'lighter';
  const r = 40 + k * 90;
  const g = ctx.createRadialGradient(x, y - 60, 0, x, y - 60, r);
  g.addColorStop(0, `rgba(255,225,130,${0.6 * (1 - k)})`);
  g.addColorStop(1, 'rgba(255,200,80,0)');
  ctx.fillStyle = g;
  ctx.beginPath();
  ctx.arc(x, y - 60, r, 0, Math.PI * 2);
  ctx.fill();
  for (let i = 0; i < 14; i++) {
    const a = (i / 14) * Math.PI * 2;
    const d = 20 + k * 110;
    ctx.fillStyle = `rgba(255,${190 + (i % 3) * 20},90,${1 - k})`;
    ctx.beginPath();
    ctx.arc(x + Math.cos(a) * d, y - 60 + Math.sin(a) * d * 0.6 - k * 30, 3 - k * 2, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();
}

/** Ночь и сумерки: картина темнеет и синеет (умножение), закат — тёплый оттенок. */
export function drawNight(ctx: CanvasRenderingContext2D, w: number, h: number, day: number, warm: number) {
  const night = 1 - day;
  if (night < 0.02 && warm < 0.02) return;
  const mix = (a: number, b: number, k: number) => Math.round(a + (b - a) * k);
  const k = Math.min(1, night * 0.85);
  let r = mix(255, 70, k);
  let g = mix(255, 88, k);
  let b = mix(255, 150, k);
  // закат/рассвет: тёплый
  r = mix(r, 255, warm * 0.2);
  g = mix(g, 196, warm * 0.35);
  b = mix(b, 150, warm * 0.45);
  ctx.save();
  ctx.globalCompositeOperation = 'multiply';
  ctx.fillStyle = `rgb(${r},${g},${b})`;
  ctx.fillRect(0, 0, w, h);
  ctx.restore();
}
